import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useLessonBookmarks = () => {
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchBookmarks();
    }
  }, [user]);

  const fetchBookmarks = async () => {
    try {
      const { data, error } = await supabase
        .from('lesson_bookmarks')
        .select('*')
        .eq('user_id', user.id)
        .order('timestamp', { ascending: true });

      if (error) throw error;
      setBookmarks(data || []);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBookmark = async (lessonId, timestamp, note = '') => {
    try {
      const { data, error } = await supabase
        .from('lesson_bookmarks')
        .insert([{
          user_id: user.id,
          lesson_id: lessonId,
          timestamp: timestamp,
          note: note.trim(),
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      setBookmarks(prev => [...prev, data].sort((a, b) => a.timestamp - b.timestamp));
      toast.success('Bookmark added');
      return { data, error: null };
    } catch (error) {
      console.error('Error adding bookmark:', error);
      toast.error('Failed to add bookmark');
      return { data: null, error };
    }
  };

  const updateBookmark = async (bookmarkId, updates) => {
    try {
      const { data, error } = await supabase
        .from('lesson_bookmarks')
        .update(updates)
        .eq('id', bookmarkId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setBookmarks(prev => 
        prev.map(bookmark => 
          bookmark.id === bookmarkId ? data : bookmark
        ).sort((a, b) => a.timestamp - b.timestamp)
      );

      return { data, error: null };
    } catch (error) {
      console.error('Error updating bookmark:', error);
      toast.error('Failed to update bookmark');
      return { data: null, error };
    }
  };

  const deleteBookmark = async (bookmarkId) => {
    try {
      const { error } = await supabase
        .from('lesson_bookmarks')
        .delete()
        .eq('id', bookmarkId)
        .eq('user_id', user.id);

      if (error) throw error;

      setBookmarks(prev => prev.filter(bookmark => bookmark.id !== bookmarkId));
      toast.success('Bookmark deleted');
      return { error: null };
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      toast.error('Failed to delete bookmark');
      return { error };
    }
  };

  return {
    bookmarks,
    loading,
    addBookmark,
    updateBookmark,
    deleteBookmark,
    refetch: fetchBookmarks
  };
};